import React, { useState, useEffect } from 'react';
import { Typography, Paper, Box, Button, Grid, CircularProgress, IconButton, Tabs, Tab, Tooltip } from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import MaintenanceForm from '../components/maintenance/MaintenanceForm';
import MaintenanceList from '../components/maintenance/MaintenanceList';
import MaintenanceRepair from '../components/maintenance/MaintenanceRepair';
import MaintenanceDetails from '../components/maintenance/MaintenanceDetails';
import api from '../utils/api';

const Maintenance = () => {
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [selectedMaintenanceId, setSelectedMaintenanceId] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [openRepair, setOpenRepair] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    fetchMaintenances();
    
    // Add event listener for maintenance-updated custom event
    window.addEventListener('maintenance-updated', fetchMaintenances);
    
    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('maintenance-updated', fetchMaintenances);
    };
  }, []);

  const fetchMaintenances = async () => {
    try {
      setLoading(true);
      const response = await api.get('/maintenance');
      setMaintenances(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching maintenances:', err);
      setError('Errore nel caricamento delle manutenzioni');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setOpenDetails(true);
  };

  const handleStartRepair = (maintenanceId) => {
    setSelectedMaintenanceId(maintenanceId);
    setOpenRepair(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setSelectedMaintenanceId(null);
  };

  const handleCloseRepair = () => {
    setOpenRepair(false);
    setSelectedMaintenanceId(null);
  };

  const handleUpdateMaintenance = () => {
    fetchMaintenances();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const getMaintenancesForTab = () => {
    switch (currentTab) {
      case 0: // In corso
        return maintenances.filter(m => !['Consegnato', 'Da Ritirare', 'Sollecitato'].includes(m.status));
      case 1: // Da ritirare
        return maintenances.filter(m => ['Da Ritirare', 'Sollecitato'].includes(m.status));
      case 2: // Completate
        return maintenances.filter(m => m.status === 'Consegnato');
      default:
        return [];
    }
  };

  const getTabTitle = () => {
    switch (currentTab) {
      case 0:
        return 'Manutenzioni in corso';
      case 1:
        return 'Manutenzioni da ritirare';
      case 2:
        return 'Manutenzioni completate';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ p: 0, m: 0, width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestione Manutenzioni
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Aggiorna lista manutenzioni">
            <IconButton
              onClick={fetchMaintenances}
              color="primary"
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setMaintenanceDialogOpen(true)}
          >
            Nuova Manutenzione
          </Button>
        </Box>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '98vw', maxWidth: '1600px', minWidth: 0, boxSizing: 'border-box', borderRadius: 0, margin: '0 auto' }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="In Corso" />
            <Tab label="Da Ritirare" />
            <Tab label="Completate" />
          </Tabs>

          <Box sx={{ p: 2 }}>
              {error ? (
                <Typography color="error">{error}</Typography>
            ) : getMaintenancesForTab().length > 0 ? (
                <MaintenanceList 
                maintenances={getMaintenancesForTab()} 
                  onViewDetails={handleViewDetails}
                  onStartRepair={handleStartRepair}
                  loading={loading}
                type={currentTab === 0 ? 'in_corso' : currentTab === 1 ? 'da_ritirare' : 'consegnate'}
                />
              ) : (
                <Typography variant="body1">
                {currentTab === 0 ? 'Nessuna manutenzione in corso. Utilizza il pulsante "Nuova Manutenzione" per aggiungerne una.' :
                 currentTab === 1 ? 'Nessuna manutenzione in attesa di ritiro.' :
                 'Nessuna manutenzione completata da visualizzare.'}
                </Typography>
              )}
              </Box>
            </Paper>
      )}

      {/* Maintenance Form Dialog */}
      <MaintenanceForm
        open={maintenanceDialogOpen}
        onClose={() => setMaintenanceDialogOpen(false)}
        onSave={async (data) => {
          try {
            const response = await api.post('/maintenance', data);
            setMaintenanceDialogOpen(false);
            await fetchMaintenances();
            return response.data; // Restituisce la manutenzione salvata
          } catch (err) {
            // Optionally, show error to user (e.g., toast or alert)
            alert(
              err.response?.data?.msg ||
              err.message ||
              'Errore durante il salvataggio della manutenzione.'
            );
            throw err; // Rilancia l'errore per gestirlo nel MaintenanceForm
          }
        }}
      />

      {/* Maintenance Details Dialog */}
      <MaintenanceDetails
        open={openDetails}
        maintenanceId={selectedMaintenanceId}
        handleClose={handleCloseDetails}
        onUpdate={handleUpdateMaintenance}
      />

      {/* Maintenance Repair Dialog */}
      <MaintenanceRepair
        open={openRepair}
        maintenanceId={selectedMaintenanceId}
        handleClose={handleCloseRepair}
        onUpdate={handleUpdateMaintenance}
      />
    </Box>
  );
};

export default Maintenance;